import React, { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, Download, FileSignature, GitCompare, PenLine, ShieldAlert } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import {
  CLAUSE_STATUS, CONFIDENCE, CONTRACT_STATUS, fmtDate, fmtDateTime, MILESTONE_STATUS,
  OCC_STATUS, RISK, RISK_FINDING_STATUS, SIG_STATUS, COMM_TYPE,
} from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, ToneChip } from "../../components/common";
import { ActivityTimeline, ApprovalCard, BlockedDialog } from "../../components/panels";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";

const TYPES = [
  "Advisory Services Agreement", "Project Management Agreement", "Development Management Agreement",
  "Asset Management Agreement", "Construction Contract", "Architecture Agreement", "Engineering Agreement",
  "Supplier Agreement", "Professional Services Agreement", "Property Management Agreement",
  "Facility Management Agreement", "Financing Agreement", "Insurance Agreement", "Data Processing Agreement",
  "Confidentiality Agreement", "Letter of Intent", "Amendment", "Other",
];

const ACTION_LABELS = {
  clasificar: "Clasificar contrato", iniciar_analisis: "Iniciar análisis IA", enviar_validacion: "Enviar a validación",
  completar_validacion: "Completar validación", enviar_revision_negocio: "Enviar a revisión de negocio",
  enviar_revision_financiera: "Enviar a revisión financiera", solicitar_cambios: "Solicitar cambios",
  marcar_cambios_incorporados: "Marcar cambios incorporados", preparar_aprobacion: "Preparar aprobación",
  solicitar_aprobacion: "Solicitar aprobación", preparar_firma: "Preparar firma", activar: "Activar contrato",
  iniciar_renovacion: "Iniciar renovación", resolver_renovacion: "Resolver renovación", iniciar_terminacion: "Iniciar terminación",
  confirmar_terminacion: "Confirmar terminación", confirmar_expiracion: "Confirmar expiración",
  archivar: "Archivar", bloquear: "Bloquear", resolver_bloqueo: "Resolver bloqueo", cancelar: "Cancelar",
};

const TABS = ["resumen", "documento", "datos", "riesgos", "obligaciones", "fechas", "economia", "revisiones", "aprobaciones", "firma", "comunicaciones", "documentos", "relacionados", "actividad"];
const TAB_LABELS = { resumen: "Resumen", documento: "Documento", datos: "Datos extraídos", riesgos: "Cláusulas y riesgos", obligaciones: "Obligaciones", fechas: "Fechas", economia: "Economía", revisiones: "Revisiones", aprobaciones: "Aprobaciones", firma: "Firma", comunicaciones: "Comunicaciones", documentos: "Documentos", relacionados: "Relacionados", actividad: "Actividad" };

const fmtMoney = (v, cur = "EUR") => (v == null ? "—" : `${Number(v).toLocaleString("es-ES")} ${cur === "EUR" ? "€" : cur}`);

export default function ContratoDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "resumen";
  const { user } = useAuth();
  const { map } = useUsers();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [blocked, setBlocked] = useState(null);
  const [payloadAction, setPayloadAction] = useState(null);
  const [form, setForm] = useState({});
  const [fieldDialog, setFieldDialog] = useState(null); // {key, action}
  const [selectedField, setSelectedField] = useState(null);
  const [sigForm, setSigForm] = useState({ signers: [{ name: "", email: "", order: 1 }], subject: "", message: "" });
  const [sigConfirm, setSigConfirm] = useState(false);
  const [sendDialogSig, setSendDialogSig] = useState(null);
  const [riskDialog, setRiskDialog] = useState(null); // {risk, action}
  const [excNote, setExcNote] = useState({});
  const [compareTarget, setCompareTarget] = useState("");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["contract", id],
    queryFn: async () => (await api.get(`/legal/contracts/${id}`)).data,
  });
  const { data: actions, refetch: refetchActions } = useQuery({
    queryKey: ["contract-actions", id],
    queryFn: async () => (await api.get(`/legal/contracts/${id}/actions`)).data,
  });
  const { data: templates } = useQuery({ queryKey: ["contract-templates"], queryFn: async () => (await api.get("/legal/templates")).data });

  const refresh = () => { refetch(); refetchActions(); qc.invalidateQueries({ queryKey: ["legal-dashboard"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };

  const runAction = async (action, payload = {}) => {
    try {
      await api.post(`/legal/contracts/${id}/actions`, { action, ...payload });
      toast.success("Acción ejecutada", { description: ACTION_LABELS[action] });
      setPayloadAction(null); setForm({});
      refresh();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) setBlocked({ action, reasons });
      else toast.error(apiError(e));
    }
  };

  const clickAction = (a) => {
    if (!a.allowed) { setBlocked({ action: a.action, reasons: a.reasons?.length ? a.reasons : [{ message: a.permission === false ? "No tienes permisos para esta acción." : "No disponible en el estado actual." }] }); return; }
    if (a.needs?.length) { setPayloadAction(a.action); setForm({}); return; }
    runAction(a.action);
  };

  const fieldAction = async (key, action, value, comment) => {
    try {
      await api.post(`/legal/contracts/${id}/fields/${key}/validate`, { action, value, comment });
      toast.success("Campo actualizado");
      setFieldDialog(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setChecklistItem = async (checklist, key, status, comment) => {
    try {
      await api.post(`/legal/contracts/${id}/review-item`, { checklist, key, status, comment });
      toast.success("Checklist actualizado");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openPdf = async () => {
    try {
      const r = await api.get(`/legal/contracts/${id}/document.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("No se pudo abrir el documento"); }
  };

  const prepareSig = async () => {
    try {
      const r = await api.post(`/legal/contracts/${id}/signature/prepare`, { ...sigForm, subject: sigForm.subject || `Firma de contrato: ${data.contract.title}` });
      toast.success("Solicitud de firma preparada");
      refresh();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) setBlocked({ action: "preparar_firma", reasons });
      else toast.error(apiError(e));
    }
  };

  const sendSig = async (sid) => {
    try {
      await api.post(`/legal/signatures/${sid}/send`, { confirm: true });
      toast.success("Enviado a firma (DocuSign mock)");
      setSendDialogSig(null); setSigConfirm(false);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const sigEvent = async (sid, event, payload = {}) => {
    try {
      await api.post(`/legal/signatures/${sid}/event`, { event, ...payload });
      toast.success("Evento registrado");
      refresh();
    } catch (e) {
      const reasons = e?.response?.data?.detail?.reasons;
      if (reasons) toast.error("Evento bloqueado", { description: reasons.map((r) => r.message).join(" · ") });
      else toast.error(apiError(e));
    }
  };

  const riskAct = async (riskId, action, payload = {}) => {
    try {
      await api.post(`/legal/risks/${riskId}/action`, { action, ...payload });
      toast.success("Riesgo actualizado");
      setRiskDialog(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const runComparison = async () => {
    if (!compareTarget) return;
    try {
      const r = await api.post("/legal/comparisons/run", { a_contract_id: id, b_model_id: compareTarget.startsWith("mtpl") ? compareTarget : undefined, b_contract_id: compareTarget.startsWith("mtpl") ? undefined : compareTarget });
      toast.success("Comparación ejecutada y guardada");
      navigate(`/legal/comparaciones/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const applyAmendment = async (amendmentId) => {
    try {
      await api.post(`/legal/contracts/${id}/apply-amendment`, { amendment_id: amendmentId });
      toast.success("Adenda aplicada: fechas y obligaciones recalculadas");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (isLoading || !data) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-700" /></div>;
  }

  const c = data.contract;
  const fields = c.extracted_fields || {};
  const counterpart = (c.counterparties || [])[0];
  const selectedEntry = selectedField ? (c.structured || []).find((e) => e.key === selectedField) : null;

  return (
    <div data-testid="contrato-detail-page">
      <Link to={`/resumen/contract/${id}`} className="mb-2 inline-block text-xs font-medium text-blue-700 hover:underline" data-testid="contract-summary-link">Executive summary →</Link>
      {/* CABECERA */}
      <div className="ts-card mb-5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-slate-400">{c.contract_number}</span>
              <Chip map={CONTRACT_STATUS} value={c.status} testid="contract-status-chip" />
              <Chip map={RISK} value={c.overall_risk} testid="contract-risk-chip" />
              <ToneChip label={c.contract_type || "Sin clasificar"} tone="muted" />
              {c.automatic_renewal && <span className="ts-chip ts-chip-warning">Renovación automática</span>}
            </div>
            <h1 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="contract-title">{c.title}</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">{c.description}</p>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
              <span>Contraparte: <strong className="text-slate-700">{counterpart?.name || "—"}</strong></span>
              <span>Owner: <strong className="text-slate-700">{userName(map, c.contract_owner)}</strong></span>
              <span>Valor: <strong className="text-slate-700">{fmtMoney(c.total_value, c.currency)}</strong></span>
              <span>Vencimiento: <strong className="text-slate-700">{fmtDate(c.current_expiration_date)}</strong></span>
              {c.notice_deadline && <span>Preaviso: <strong className="text-red-600">{fmtDate(c.notice_deadline)}</strong></span>}
            </div>
            {c.next_action && <p className="mt-2 text-sm text-slate-600"><span className="ts-caption mr-2">Próximo paso</span>{c.next_action}{c.next_action_owner ? ` — ${userName(map, c.next_action_owner)}` : ""}</p>}
            {c.blocked_reason && <p className="mt-2 rounded-sm border border-red-200 bg-red-50 p-2 text-sm text-red-700"><strong>Bloqueado:</strong> {c.blocked_reason}</p>}
          </div>
          <div className="flex max-w-sm flex-wrap gap-1.5" data-testid="contract-actions-bar">
            {(actions || []).filter((a) => a.allowed || ["solicitar_aprobacion", "completar_validacion", "activar", "resolver_renovacion", "confirmar_terminacion"].includes(a.action)).slice(0, 8).map((a) => (
              <Button key={a.action} size="sm" variant={a.allowed ? (["cancelar", "bloquear", "iniciar_terminacion"].includes(a.action) ? "destructive" : "default") : "outline"} onClick={() => clickAction(a)} data-testid={`contract-action-${a.action}`}>
                {ACTION_LABELS[a.action]}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-4 flex h-auto flex-wrap justify-start" data-testid="contract-tabs">
          {TABS.map((t) => <TabsTrigger key={t} value={t} data-testid={`contract-tab-${t}`}>{TAB_LABELS[t]}</TabsTrigger>)}
        </TabsList>

        <TabsContent value="resumen">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <div className="ts-card p-4"><p className="ts-caption mb-2">Qué es</p><p className="text-sm text-slate-700">{c.contract_type} · {c.jurisdiction} · {c.governing_law}</p><p className="mt-1 text-sm text-slate-600">{c.description}</p></div>
            <div className="ts-card p-4"><p className="ts-caption mb-2">Con quién</p><p className="text-sm text-slate-700">{counterpart?.name || "—"} {counterpart?.nif ? `· NIF ${counterpart.nif}` : ""}</p><p className="mt-1 text-xs text-slate-500">Rol: {counterpart?.role || "—"} · Firmante: {counterpart?.signatory || "—"}</p></div>
            <div className="ts-card p-4"><p className="ts-caption mb-2">Cuánto vale</p><p className="font-display text-2xl font-bold text-slate-900">{fmtMoney(c.total_value, c.currency)}</p><p className="text-xs text-slate-500">{c.payment_terms} · {c.billing_frequency} · {c.tax_treatment}</p></div>
            <div className="ts-card p-4"><p className="ts-caption mb-2">Cuándo</p><p className="text-sm text-slate-700">Inicio: {fmtDate(c.start_date)} · Vence: {fmtDate(c.current_expiration_date)}</p><p className="text-xs text-slate-500">Preaviso: {c.notice_period} días ({c.notice_method}){c.automatic_renewal ? " · Renovación automática" : ""}</p></div>
            <div className="ts-card p-4"><p className="ts-caption mb-2">Riesgos</p><div className="flex flex-wrap gap-1">{[["Legal", c.legal_risk], ["Financiero", c.financial_risk], ["Operativo", c.operational_risk], ["Datos", c.data_protection_risk], ["Seguros", c.insurance_risk]].map(([l, r]) => <span key={l} className="text-xs">{l}: <Chip map={RISK} value={r || "bajo"} /></span>)}</div>{c.risk_summary && <p className="mt-2 text-xs text-amber-700">{c.risk_summary}</p>}</div>
            <div className="ts-card p-4"><p className="ts-caption mb-2">Qué falta / quién actúa</p><p className="text-sm text-slate-700">{c.next_action || "—"}</p><p className="mt-1 text-xs text-slate-500">Responsable: {userName(map, c.next_action_owner || c.contract_owner)}</p></div>
          </div>
        </TabsContent>

        <TabsContent value="documento">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="ts-card flex flex-col items-center justify-center p-8 text-center">
              <FileSignature className="mb-3 h-10 w-10 text-slate-300" />
              <p className="font-medium text-slate-800">{c.contract_number} · versión {c.current_version}</p>
              <p className="mt-1 text-xs font-semibold text-red-600">DEMO — DOCUMENTO FICTICIO · NO CONSTITUYE DOCUMENTACIÓN LEGAL REAL</p>
              <Button className="mt-4" variant="outline" onClick={openPdf} data-testid="open-contract-pdf"><Download className="mr-1 h-4 w-4" /> Abrir PDF del contrato</Button>
            </div>
            <div className="ts-card max-h-[60vh] overflow-y-auto p-4">
              <p className="ts-caption mb-2">Contenido estructurado (cláusulas)</p>
              {(c.structured || []).map((e, i) => (
                <div key={i} id={`clause-${e.key}`} className={`mb-3 rounded-sm border p-3 transition-colors ${selectedField === e.key ? "border-blue-400 bg-blue-50/50" : "border-slate-100"}`} data-testid={`doc-clause-${e.key}`}>
                  <p className="text-xs font-semibold text-blue-800">{e.clause} <span className="font-normal text-slate-400">· pág. {e.page}</span></p>
                  <p className="mt-1 text-sm text-slate-700">{e.text}</p>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="datos">
          <div className="grid gap-4 lg:grid-cols-2" data-testid="validation-screen">
            <div className="ts-card max-h-[70vh] overflow-y-auto p-4">
              <p className="ts-caption mb-2">Documento (fuente)</p>
              {(c.structured || []).map((e, i) => (
                <div key={i} className={`mb-2 cursor-pointer rounded-sm border p-2.5 transition-colors ${selectedField === e.key ? "border-blue-500 bg-blue-50" : "border-transparent hover:bg-slate-50"}`} onClick={() => setSelectedField(e.key)} data-testid={`source-fragment-${e.key}`}>
                  <p className="text-[10px] font-semibold uppercase text-slate-400">{e.clause} · pág. {e.page}</p>
                  <p className="text-xs text-slate-700">{e.text}</p>
                </div>
              ))}
            </div>
            <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
              {!Object.keys(fields).length ? (
                <EmptyState title="Sin análisis ejecutado" description="Usa la acción «Iniciar análisis IA» en la cabecera." testid="no-analysis" />
              ) : Object.entries(fields).map(([key, f]) => (
                <div key={key} className={`ts-card p-3 ${selectedField === key ? "ring-2 ring-blue-400" : ""}`} data-testid={`field-${key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => setSelectedField(key)} className="text-sm font-semibold capitalize text-slate-900 hover:text-blue-700">{key.replace(/_/g, " ")}</button>
                    {f.material && <span className="ts-chip ts-chip-danger">material</span>}
                    <Chip map={CONFIDENCE} value={f.level} testid={`field-confidence-${key}`} />
                    <span className="font-mono text-[10px] text-slate-400">{f.confidence}</span>
                    <ToneChip label={f.validation_status} tone={["validado", "corregido"].includes(f.validation_status) ? "success" : f.validation_status === "rechazado" ? "danger" : "muted"} />
                  </div>
                  <p className="mt-1 text-sm text-slate-700">{f.value ?? <span className="font-semibold text-slate-400">NO IDENTIFICADO</span>}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">{f.clause} · pág. {f.page}{f.validation_status === "corregido" && f.initial_value !== f.value ? ` · valor inicial: ${f.initial_value}` : ""}</p>
                  {f.reviewer_comment && <p className="mt-0.5 text-xs text-slate-500">Nota: {f.reviewer_comment}</p>}
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => fieldAction(key, "validar")} data-testid={`field-validate-${key}`}><Check className="mr-1 h-3 w-3" /> Validar</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFieldDialog({ key, action: "corregir", value: f.value })} data-testid={`field-correct-${key}`}>Corregir</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fieldAction(key, "no_aplicable")} data-testid={`field-na-${key}`}>No aplica</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => fieldAction(key, "rechazar")} data-testid={`field-reject-${key}`}>Rechazar</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fieldAction(key, "solicitar_revision", null, "Segunda revisión solicitada desde la pantalla de validación")} data-testid={`field-second-${key}`}>2ª revisión</Button>
                  </div>
                </div>
              ))}
              {Object.keys(fields).length > 0 && (
                <div className="sticky bottom-0 rounded-sm border border-slate-200 bg-white p-3 shadow-sm">
                  <Button className="w-full" onClick={() => runAction("completar_validacion")} data-testid="complete-validation-button">Completar validación y enviar a revisión jurídica</Button>
                  <p className="mt-1 text-center text-[10px] text-slate-400">Bloqueado si quedan campos materiales sin validar, baja confianza o contradicciones.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="riesgos">
          <div className="mb-4 flex flex-wrap items-end gap-2">
            <div>
              <Label>Comparar con…</Label>
              <Select value={compareTarget} onValueChange={setCompareTarget}>
                <SelectTrigger className="w-72" data-testid="compare-target-select"><SelectValue placeholder="Modelo corporativo o contrato…" /></SelectTrigger>
                <SelectContent>
                  {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>Modelo: {t.name}</SelectItem>)}
                  {c.parent_contract && <SelectItem value={c.parent_contract}>Contrato padre</SelectItem>}
                  {(c.amendments || []).map((a) => <SelectItem key={a} value={a}>Adenda {a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={runComparison} disabled={!compareTarget} data-testid="run-comparison"><GitCompare className="mr-1 h-4 w-4" /> Ejecutar comparación</Button>
          </div>

          {data.exceptions.length > 0 && (
            <div className="mb-4 space-y-2" data-testid="exceptions-list">
              <p className="ts-caption">Excepciones</p>
              {data.exceptions.map((ex) => (
                <div key={ex.id} className="ts-card flex flex-wrap items-center gap-3 p-3" data-testid={`exception-${ex.id}`}>
                  <AlertTriangle className={`h-4 w-4 ${ex.severity === "alta" || ex.severity === "critica" ? "text-red-500" : "text-amber-500"}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800">{ex.description}</p>
                    <p className="text-xs text-slate-500">Severidad: {ex.severity} · Responsable: {userName(map, ex.owner_id)} · Acción: {ex.required_action}</p>
                  </div>
                  {ex.status === "abierta" ? (
                    <div className="flex gap-1.5">
                      <Input placeholder="Nota de resolución…" value={excNote[ex.id] || ""} onChange={(e) => setExcNote({ ...excNote, [ex.id]: e.target.value })} className="h-8 w-52" data-testid={`exception-note-${ex.id}`} />
                      <Button size="sm" variant="outline" disabled={!excNote[ex.id]?.trim()} onClick={async () => { await api.post(`/legal/exceptions/${ex.id}/resolve`, { note: excNote[ex.id] }); toast.success("Excepción resuelta"); refresh(); }} data-testid={`exception-resolve-${ex.id}`}>Resolver</Button>
                    </div>
                  ) : <ToneChip label="Resuelta" tone="success" />}
                </div>
              ))}
            </div>
          )}

          <p className="ts-caption mb-2">Hallazgos de riesgo</p>
          {!data.risks.length ? <EmptyState title="Sin riesgos identificados" description="Los análisis y comparaciones que detecten desviaciones aparecerán aquí." /> : (
            <div className="space-y-2">
              {data.risks.map((r) => (
                <div key={r.id} className="ts-card p-4" data-testid={`risk-${r.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <ShieldAlert className={`h-4 w-4 ${r.severity === "critico" ? "text-red-600" : r.severity === "alto" ? "text-amber-600" : "text-slate-400"}`} />
                        <p className="text-sm font-medium text-slate-900">{r.description}</p>
                        <ToneChip label={r.severity} tone={r.severity === "critico" ? "danger" : r.severity === "alto" ? "warning" : "muted"} />
                        <Chip map={RISK_FINDING_STATUS} value={r.status} testid={`risk-status-${r.id}`} />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{r.clause} · Fuente: {r.source} · Política: {r.policy_comparison} · Owner: {userName(map, r.owner_id)}</p>
                      <p className="mt-0.5 text-xs text-slate-600">Recomendación: {r.recommendation}</p>
                      {r.acceptance && <p className="mt-1 rounded-sm bg-sky-50 p-1.5 text-xs text-sky-800">Aceptado por {userName(map, r.acceptance.approver_id)}: {r.acceptance.motivo} (impacto: {r.acceptance.impacto})</p>}
                      {r.resolution && <p className="mt-1 rounded-sm bg-emerald-50 p-1.5 text-xs text-emerald-800">Resuelto por {userName(map, r.resolution.reviewer_id)}: {r.resolution.explicacion} — {r.resolution.accion_realizada}</p>}
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {r.status === "detectado" && <Button size="sm" variant="outline" onClick={() => riskAct(r.id, "pendiente_revision")} data-testid={`risk-review-${r.id}`}>Revisar</Button>}
                      {r.status === "pendiente_revision" && <Button size="sm" variant="outline" onClick={() => riskAct(r.id, "confirmado")} data-testid={`risk-confirm-${r.id}`}>Confirmar</Button>}
                      {["confirmado", "mitigacion_requerida"].includes(r.status) && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setRiskDialog({ risk: r, action: "aceptado" })} data-testid={`risk-accept-${r.id}`}>Aceptar riesgo</Button>
                          <Button size="sm" onClick={() => setRiskDialog({ risk: r, action: "resuelto" })} data-testid={`risk-resolve-${r.id}`}>Resolver</Button>
                        </>
                      )}
                      {r.status === "confirmado" && <Button size="sm" variant="ghost" onClick={() => riskAct(r.id, "mitigacion_requerida")} data-testid={`risk-mitigate-${r.id}`}>Requerir mitigación</Button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="obligaciones">
          {!data.obligations.length ? (
            <EmptyState title="Sin obligaciones extraídas" description="Las obligaciones se extraen del análisis IA y se validan antes de activarse." />
          ) : (
            <div className="space-y-3">
              {data.obligations.map((ob) => (
                <div key={ob.id} className="ts-card p-4" data-testid={`obligation-${ob.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-900">{ob.title}</p>
                    <Chip map={OCC_STATUS} value={ob.status} />
                    <ToneChip label={ob.obligation_type} tone="info" />
                    {ob.frequency && <ToneChip label={ob.frequency} tone="muted" />}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Parte obligada: {ob.obligated_party} · Responsable: <strong>{userName(map, ob.internal_owner)}</strong> · Revisor: {userName(map, ob.reviewer)} · Fuente: {ob.source_clause} pág. {ob.source_page}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">Consecuencia: {ob.consequence} · Escalación: {ob.escalation_rule}</p>
                  {ob.status === "candidata" && (
                    <Button size="sm" variant="outline" className="mt-2" onClick={async () => { await api.post(`/legal/obligations/${ob.id}/validate`); toast.success("Obligación validada"); refresh(); }} data-testid={`validate-obl-${ob.id}`}>Validar y activar</Button>
                  )}
                  <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-2">
                    {data.occurrences.filter((o) => o.obligation_id === ob.id).map((o) => (
                      <div key={o.id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`occ-row-${o.id}`}>
                        <Chip map={OCC_STATUS} value={o.status} />
                        <span className="text-slate-700">{o.title}</span>
                        <span className="text-slate-400">vence {fmtDate(o.due_date)}</span>
                        {["programada", "proxima", "pendiente_evidencia", "vencida"].includes(o.status) && (
                          <a href="/legal/obligaciones" className="font-medium text-blue-700 hover:underline">Gestionar →</a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" className="mt-4" onClick={() => navigate("/legal/obligaciones")} data-testid="goto-obligations">Abrir gestión de obligaciones</Button>
        </TabsContent>

        <TabsContent value="fechas">
          <div className="ts-card p-5">
            <p className="ts-caption mb-4">Calendario contractual</p>
            <ol className="relative space-y-4 border-l border-slate-200 pl-5">
              {[["Recepción", c.received_date], ["Firma", c.signature_date], ["Efecto", c.effective_date], ["Inicio", c.start_date],
                ["Revisión de renovación", c.renewal_review_date], ["Preaviso límite", c.notice_deadline],
                ["Vencimiento inicial", c.initial_expiration_date], ["Vencimiento vigente", c.current_expiration_date], ["Terminación", c.termination_date]]
                .filter(([, v]) => v).map(([label, v]) => (
                  <li key={label} className="relative">
                    <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-600" />
                    <p className="text-sm font-medium text-slate-800">{label}</p>
                    <p className="text-xs text-slate-500">{fmtDate(v)}</p>
                  </li>
                ))}
            </ol>
            {(c.renewal_log || []).length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-3">
                <p className="ts-caption mb-2">Histórico de renovaciones</p>
                {c.renewal_log.map((r, i) => <p key={i} className="text-xs text-slate-600">{fmtDateTime(r.at)} — {r.decision} por {userName(map, r.by)} → nuevo vencimiento {fmtDate(r.new_expiration)}</p>)}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="economia">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="ts-card p-4">
              <p className="ts-caption mb-2">Condiciones</p>
              <dl className="space-y-1 text-sm">
                {[["Valor total", fmtMoney(c.total_value, c.currency)], ["Tipo de honorario", c.fee_type], ["Pagos", c.payment_terms], ["Periodicidad", c.billing_frequency], ["Indexación", c.indexation], ["Impuestos", c.tax_treatment], ["Retención", c.retention || "—"]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-800">{v}</dd></div>
                ))}
              </dl>
            </div>
            <div className="ts-card p-4 lg:col-span-2">
              <p className="ts-caption mb-2">Hitos económicos (calendario de facturación)</p>
              {!data.billing.length ? <p className="text-sm text-slate-400">Se generan al activar el contrato.</p> : (
                <div className="space-y-2">
                  {data.billing.map((m) => (
                    <div key={m.id} className="flex flex-wrap items-center gap-2 rounded-sm border border-slate-100 p-2.5" data-testid={`milestone-${m.id}`}>
                      <Chip map={MILESTONE_STATUS} value={m.status} />
                      <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{m.milestone}</span>
                      <span className="text-xs text-slate-500">{fmtDate(m.planned_date)}</span>
                      <span className="font-mono text-sm font-semibold text-slate-900">{fmtMoney(m.amount, m.currency)}</span>
                      {m.status === "bloqueado" && <span className="text-xs text-red-600">{m.block_reason}</span>}
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-3 text-xs text-slate-400">Este calendario alimentará el área Finance en la fase correspondiente.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="revisiones">
          {!c.reviews ? (
            <EmptyState title="Sin checklists" description="Los checklists se generan al entrar en revisión." />
          ) : (
            <div className="grid gap-4 lg:grid-cols-3">
              {Object.entries(c.reviews).map(([name, checklist]) => (
                <div key={name} className="ts-card p-4" data-testid={`checklist-${name}`}>
                  <p className="ts-caption mb-2">Checklist {name === "legal" ? "jurídico" : name === "business" ? "de negocio" : "financiero"} · {userName(map, checklist.reviewer)}</p>
                  <div className="space-y-2">
                    {(checklist.items || []).map((item) => (
                      <div key={item.key} className="rounded-sm border border-slate-100 p-2" data-testid={`checklist-item-${name}-${item.key}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-medium text-slate-700">{item.label}</p>
                          <ToneChip label={item.status} tone={item.status === "ok" ? "success" : item.status === "no_conforme" ? "danger" : item.status === "no_aplica" ? "muted" : "warning"} />
                        </div>
                        {item.comment && <p className="mt-1 text-[10px] text-slate-500">{item.comment} — {userName(map, item.by)}</p>}
                        <div className="mt-1.5 flex gap-1">
                          <button onClick={() => setChecklistItem(name, item.key, "ok")} className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100" data-testid={`check-ok-${name}-${item.key}`}>Conforme</button>
                          <button onClick={() => { const cm = window.prompt("Comentario (obligatorio para no conforme):"); if (cm) setChecklistItem(name, item.key, "no_conforme", cm); }} className="rounded-sm bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700 hover:bg-red-100" data-testid={`check-nc-${name}-${item.key}`}>No conforme</button>
                          <button onClick={() => setChecklistItem(name, item.key, "no_aplica")} className="rounded-sm bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-100" data-testid={`check-na-${name}-${item.key}`}>N/A</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="aprobaciones">
          {!data.approvals.length ? (
            <EmptyState title="Sin aprobaciones" description="Solicita aprobación desde la cabecera cuando los controles estén superados. La matriz de aprobación asigna aprobadores por valor y riesgo." testid="contract-approvals-empty" />
          ) : (
            <div className="space-y-3">{data.approvals.map((a) => <ApprovalCard key={a.id} appr={{ ...a, expediente: data.expediente }} me={user} onChanged={refresh} />)}</div>
          )}
        </TabsContent>

        <TabsContent value="firma">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="ts-card p-4" data-testid="signature-prepare-panel">
              <p className="ts-caption mb-2">Preparar solicitud de firma (DocuSign mock)</p>
              {sigForm.signers.map((s, i) => (
                <div key={i} className="mb-2 grid grid-cols-3 gap-2">
                  <Input placeholder="Nombre" value={s.name} onChange={(e) => { const sg = [...sigForm.signers]; sg[i].name = e.target.value; setSigForm({ ...sigForm, signers: sg }); }} data-testid={`signer-name-${i}`} />
                  <Input placeholder="Email" value={s.email} onChange={(e) => { const sg = [...sigForm.signers]; sg[i].email = e.target.value; setSigForm({ ...sigForm, signers: sg }); }} data-testid={`signer-email-${i}`} />
                  <Input type="number" placeholder="Orden" value={s.order} onChange={(e) => { const sg = [...sigForm.signers]; sg[i].order = Number(e.target.value); setSigForm({ ...sigForm, signers: sg }); }} data-testid={`signer-order-${i}`} />
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => setSigForm({ ...sigForm, signers: [...sigForm.signers, { name: "", email: "", order: sigForm.signers.length + 1 }] })} data-testid="add-signer">+ Añadir firmante</Button>
              <Textarea className="mt-2" placeholder="Mensaje para los firmantes…" value={sigForm.message} onChange={(e) => setSigForm({ ...sigForm, message: e.target.value })} data-testid="signature-message" />
              <Button className="mt-3 w-full" variant="outline" onClick={prepareSig} disabled={c.status !== "preparado_firma"} data-testid="prepare-signature">
                <PenLine className="mr-1 h-4 w-4" /> Preparar solicitud {c.status !== "preparado_firma" ? `(requiere estado «Preparado para firma»)` : ""}
              </Button>
              {c.signed_document_id && <p className="mt-2 text-xs text-emerald-700">Documento firmado y certificado archivados como versiones inmutables.</p>}
            </div>
            <div className="space-y-3">
              {!data.signatures.length ? <EmptyState title="Sin solicitudes de firma" description="Prepara una solicitud cuando el contrato esté aprobado." /> : data.signatures.map((s) => (
                <div key={s.id} className="ts-card p-4" data-testid={`signature-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip map={SIG_STATUS} value={s.status} testid={`signature-status-${s.id}`} />
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{s.subject}</p>
                    <span className="text-xs text-slate-400">v{s.document_version} · expira {fmtDate(s.expires_at)}</span>
                  </div>
                  <div className="mt-2 space-y-1">
                    {s.signers.map((sg) => (
                      <div key={sg.email} className="flex items-center gap-2 text-xs">
                        <span className={`h-2 w-2 rounded-full ${sg.status === "firmado" ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <span className="text-slate-700">{sg.order}. {sg.name} &lt;{sg.email}&gt;</span>
                        <span className="text-slate-400">{sg.status}{sg.signed_at ? ` · ${fmtDate(sg.signed_at)}` : ""}</span>
                        {["enviado", "visualizado", "parcialmente_firmado"].includes(s.status) && sg.status !== "firmado" && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => sigEvent(s.id, "firmar", { signer_email: sg.email })} data-testid={`simulate-sign-${s.id}-${sg.order}`}>Simular firma</Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {s.status === "preparado" && <Button size="sm" onClick={() => setSendDialogSig(s)} data-testid={`send-signature-${s.id}`}>Enviar a firma</Button>}
                    {["enviado", "visualizado", "parcialmente_firmado"].includes(s.status) && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => sigEvent(s.id, "visualizado")} data-testid={`simulate-view-${s.id}`}>Simular visualización</Button>
                        <Button size="sm" variant="ghost" onClick={() => sigEvent(s.id, "recordar")} data-testid={`remind-signature-${s.id}`}>Recordar</Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { const m = window.prompt("Motivo del rechazo de firma:"); if (m) sigEvent(s.id, "rechazar", { motivo: m }); }} data-testid={`simulate-reject-${s.id}`}>Simular rechazo</Button>
                        <Button size="sm" variant="ghost" onClick={() => sigEvent(s.id, "cancelar")} data-testid={`cancel-signature-${s.id}`}>Cancelar</Button>
                      </>
                    )}
                    {s.status === "completado" && (
                      <Button size="sm" variant="outline" onClick={openPdf} data-testid={`download-signed-${s.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Descargar firmado + certificado</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="comunicaciones">
          {!data.communications.length ? (
            <EmptyState title="Sin comunicaciones" description="Asocia comunicaciones desde la bandeja de Comunicaciones." action={<Button variant="outline" onClick={() => navigate("/comunicaciones")}>Ir a Comunicaciones</Button>} />
          ) : (
            <div className="space-y-2">
              {data.communications.map((cm) => (
                <div key={cm.id} className="ts-card p-3" data-testid={`contract-comm-${cm.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <ToneChip label={COMM_TYPE[cm.type] || cm.type} tone={cm.direction === "recibida" ? "info" : "muted"} />
                    <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{cm.subject}</p>
                    <span className="text-xs text-slate-400">{fmtDateTime(cm.date)}</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{cm.sender_name} · {cm.content?.slice(0, 120)}…</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documentos">
          {!data.documents.length ? <EmptyState title="Sin documentos" /> : (
            <div className="space-y-2">
              {data.documents.map((d) => (
                <div key={d.id} className="ts-card flex flex-wrap items-center gap-3 p-3" data-testid={`contract-doc-${d.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800">{d.name} {d.immutable && <ToneChip label="inmutable" tone="success" />}</p>
                    <p className="text-xs text-slate-500">v{d.version} · {d.doc_type} · {userName(map, d.owner_id)} · {fmtDate(d.date)} · <span className="font-medium text-red-600">DEMO — DOCUMENTO FICTICIO</span></p>
                  </div>
                  <Button size="sm" variant="outline" onClick={openPdf} data-testid={`open-doc-${d.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Abrir PDF</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="relacionados">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="ts-card p-4">
              <p className="ts-caption mb-2">Vínculos</p>
              {data.expediente && <p className="text-sm"><Link to={`/expedientes/${data.expediente.id}`} className="text-blue-700 hover:underline" data-testid="link-expediente">Expediente {data.expediente.code}</Link></p>}
              {data.parent && <p className="mt-1 text-sm"><Link to={`/legal/contratos/${data.parent.id}`} className="text-blue-700 hover:underline" data-testid="link-parent">Contrato padre: {data.parent.contract_number}</Link></p>}
              {(c.amendments || []).map((a) => <p key={a} className="mt-1 text-sm"><Link to={`/legal/contratos/${a}`} className="text-blue-700 hover:underline">Adenda: {a}</Link></p>)}
              {c.nda?.related_opportunity && <p className="mt-1 text-sm"><Link to={`/expedientes/${c.nda.related_opportunity}`} className="text-blue-700 hover:underline">Oportunidad relacionada</Link></p>}
              {c.nda && <p className="mt-2 text-xs text-slate-600">Acceso a data room (demo): <strong>{c.nda.data_room_access ? "ACTIVO" : "inactivo"}</strong></p>}
              {c.loi && (
                <div className="mt-2 space-y-1 text-xs text-slate-600">
                  <p>Precio indicativo: <strong>{fmtMoney(c.loi.indicative_price)}</strong> · Depósito: {fmtMoney(c.loi.deposit)}</p>
                  <p>Exclusividad: {fmtDate(c.loi.exclusivity_start)} → {fmtDate(c.loi.exclusivity_end)} · Due Diligence: {fmtDate(c.loi.due_diligence_start)} → {fmtDate(c.loi.due_diligence_end)}</p>
                  <p>Vinculante: {c.loi.binding_sections} · Próximo gate: {c.loi.next_gate}</p>
                </div>
              )}
              {c.parent_contract == null && c.contract_type === "Amendment" && <p className="text-xs text-slate-400">Adenda sin padre vinculado.</p>}
              {c.contract_type === "Amendment" && data.parent && ["firmado", "activo"].includes(c.status) && (
                <Button size="sm" className="mt-3" onClick={() => applyAmendment(c.id)} data-testid="apply-amendment-button">Aplicar adenda al contrato padre</Button>
              )}
              {data.parent && data.parent.amendments?.includes(c.id) === false && null}
            </div>
            <div className="ts-card p-4">
              <p className="ts-caption mb-2">Comparaciones guardadas</p>
              {!data.comparisons.length ? <p className="text-xs text-slate-400">Ninguna todavía.</p> : data.comparisons.map((cmp) => (
                <Link key={cmp.id} to={`/legal/comparaciones/${cmp.id}`} className="mb-1 block text-xs text-blue-700 hover:underline" data-testid={`comparison-link-${cmp.id}`}>
                  {cmp.kind}: {cmp.a_label.slice(0, 40)} vs {cmp.b_label.slice(0, 30)} — {cmp.differences.length} diferencias
                </Link>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="actividad">
          <div className="ts-card p-5"><ActivityTimeline items={data.activity} testid="contract-activity" /></div>
        </TabsContent>
      </Tabs>

      {/* Diálogos */}
      <BlockedDialog open={!!blocked} onClose={() => setBlocked(null)} reasons={blocked?.reasons} title={`Acción bloqueada: ${ACTION_LABELS[blocked?.action] || ""}`} />

      <Dialog open={!!payloadAction} onOpenChange={() => setPayloadAction(null)}>
        <DialogContent data-testid="contract-action-dialog">
          <DialogHeader><DialogTitle>{ACTION_LABELS[payloadAction]}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {payloadAction === "clasificar" && (
              <div>
                <Label>Tipo de contrato *</Label>
                <Select value={form.contract_type || ""} onValueChange={(v) => setForm({ ...form, contract_type: v })}>
                  <SelectTrigger data-testid="classify-type-select"><SelectValue placeholder="Seleccionar tipo…" /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <p className="mt-1 text-xs text-slate-500">Al clasificar se ejecuta automáticamente el análisis IA de extracción.</p>
              </div>
            )}
            {["solicitar_cambios", "bloquear", "cancelar", "iniciar_terminacion"].includes(payloadAction) && (
              <div><Label>Motivo (obligatorio)</Label><Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="action-motivo" /></div>
            )}
            {payloadAction === "resolver_bloqueo" && (
              <div><Label>Resolución del bloqueo</Label><Textarea value={form.resolucion || ""} onChange={(e) => setForm({ ...form, resolucion: e.target.value })} data-testid="action-resolucion" /></div>
            )}
            {payloadAction === "solicitar_aprobacion" && (
              <div><Label>Información para los aprobadores</Label><Textarea value={form.info || ""} onChange={(e) => setForm({ ...form, info: e.target.value })} placeholder="La matriz asigna aprobadores según valor y riesgo…" data-testid="action-info" /></div>
            )}
            {payloadAction === "resolver_renovacion" && (
              <>
                <div>
                  <Label>Decisión *</Label>
                  <Select value={form.decision || ""} onValueChange={(v) => setForm({ ...form, decision: v })}>
                    <SelectTrigger data-testid="renewal-decision"><SelectValue placeholder="Decidir…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="renovar">Renovar sin cambios</SelectItem>
                      <SelectItem value="renegociar">Renegociar</SelectItem>
                      <SelectItem value="no_renovar">No renovar</SelectItem>
                      <SelectItem value="terminar_anticipado">Terminar anticipadamente</SelectItem>
                      <SelectItem value="pendiente_informacion">Pendiente de información</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {["renovar", "renegociar"].includes(form.decision) && (
                  <div><Label>Evidencia de la decisión *</Label><Input value={form.evidence_ref || ""} onChange={(e) => setForm({ ...form, evidence_ref: e.target.value })} placeholder="Acta de comité, email, documento…" data-testid="renewal-evidence" /></div>
                )}
                {["no_renovar", "terminar_anticipado"].includes(form.decision) && (
                  <div>
                    <Label>Motivo *</Label>
                    <Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="renewal-motivo" />
                    <p className="mt-1 text-xs text-slate-500">Se preparará la comunicación conforme al método requerido: <strong>{c.notice_method}</strong>. La terminación exigirá aprobación y evidencia de envío.</p>
                  </div>
                )}
              </>
            )}
            <Button className="w-full" onClick={() => runAction(payloadAction, form)} data-testid="contract-action-confirm">Confirmar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fieldDialog} onOpenChange={() => setFieldDialog(null)}>
        <DialogContent data-testid="field-correct-dialog">
          <DialogHeader><DialogTitle>Corregir campo «{fieldDialog?.key}»</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {selectedEntry && <p className="rounded-sm bg-slate-50 p-2 text-xs text-slate-600">Fuente ({selectedEntry.clause}, pág. {selectedEntry.page}): {selectedEntry.text}</p>}
            <div><Label>Valor corregido</Label><Input value={fieldDialog?.value || ""} onChange={(e) => setFieldDialog({ ...fieldDialog, value: e.target.value })} data-testid="field-correct-value" /></div>
            <div><Label>Comentario</Label><Input value={fieldDialog?.comment || ""} onChange={(e) => setFieldDialog({ ...fieldDialog, comment: e.target.value })} data-testid="field-correct-comment" /></div>
            <Button className="w-full" onClick={() => fieldAction(fieldDialog.key, "corregir", fieldDialog.value, fieldDialog.comment)} data-testid="field-correct-confirm">Guardar corrección (se conserva el valor inicial)</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendDialogSig} onOpenChange={() => setSendDialogSig(null)}>
        <DialogContent data-testid="send-signature-dialog">
          <DialogHeader><DialogTitle>Confirmar envío a firma</DialogTitle></DialogHeader>
          {sendDialogSig && (
            <div className="space-y-3">
              <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                <p><strong>Documento:</strong> {c.contract_number} v{sendDialogSig.document_version}</p>
                <p><strong>Asunto:</strong> {sendDialogSig.subject}</p>
                <p><strong>Expira:</strong> {fmtDate(sendDialogSig.expires_at)}</p>
                <p className="mt-1"><strong>Destinatarios y orden:</strong></p>
                {sendDialogSig.signers.map((s) => <p key={s.email}>· {s.order}. {s.name} &lt;{s.email}&gt; ({s.role})</p>)}
                {sendDialogSig.message && <p className="mt-1"><strong>Mensaje:</strong> {sendDialogSig.message}</p>}
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={sigConfirm} onCheckedChange={setSigConfirm} data-testid="send-signature-confirm" />
                Confirmo el envío de esta solicitud de firma
              </label>
              <Button className="w-full" disabled={!sigConfirm} onClick={() => sendSig(sendDialogSig.id)} data-testid="send-signature-submit">Enviar (DocuSign mock)</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!riskDialog} onOpenChange={() => setRiskDialog(null)}>
        <DialogContent data-testid="risk-action-dialog">
          <DialogHeader><DialogTitle>{riskDialog?.action === "aceptado" ? "Aceptar riesgo" : "Resolver riesgo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {riskDialog?.action === "aceptado" ? (
              <>
                <div><Label>Motivo *</Label><Textarea value={form.motivo || ""} onChange={(e) => setForm({ ...form, motivo: e.target.value })} data-testid="risk-accept-motivo" /></div>
                <div>
                  <Label>Aprobador *</Label>
                  <Select value={form.approver_id || ""} onValueChange={(v) => setForm({ ...form, approver_id: v })}>
                    <SelectTrigger data-testid="risk-accept-approver"><SelectValue placeholder="Seleccionar…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="usr_02">Elena Torres — Managing Director</SelectItem>
                      <SelectItem value="usr_03">Carlos Vega — Legal Director</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Impacto conocido</Label><Input value={form.impacto || ""} onChange={(e) => setForm({ ...form, impacto: e.target.value })} data-testid="risk-accept-impact" /></div>
                <Button className="w-full" disabled={!form.motivo?.trim() || !form.approver_id} onClick={() => riskAct(riskDialog.risk.id, "aceptado", form)} data-testid="risk-accept-confirm">Aceptar riesgo</Button>
              </>
            ) : (
              <>
                <div><Label>Explicación *</Label><Textarea value={form.explicacion || ""} onChange={(e) => setForm({ ...form, explicacion: e.target.value })} data-testid="risk-resolve-expl" /></div>
                <div><Label>Acción realizada * (evidencia o versión corregida)</Label><Input value={form.accion_realizada || ""} onChange={(e) => setForm({ ...form, accion_realizada: e.target.value })} data-testid="risk-resolve-action" /></div>
                <Button className="w-full" disabled={!form.explicacion?.trim() || !form.accion_realizada?.trim()} onClick={() => riskAct(riskDialog.risk.id, "resuelto", form)} data-testid="risk-resolve-confirm">Resolver riesgo</Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
